import type { Patch } from "../engine/types";

// Shared "clipboard" of sounds: a fixed 4-wide x 16-tall grid (64 slots),
// persisted in localStorage so it's available across the whole tool. A slot is
// either a synth patch (replayable at its captured note) or a recorded sample.
export const BANKS = 4;
export const BANK_COLS = 2;
export const BANK_ROWS = 8;
export const SLOTS_PER_BANK = BANK_COLS * BANK_ROWS; // 16
export const PALETTE_SIZE = BANKS * SLOTS_PER_BANK; // 64

export interface PatchSlot {
  id: string;
  kind: "patch";
  name: string;
  color: string;
  patch: Patch;
  /** The note last played when this sound was captured; used for preview. */
  lastMidi: number;
  /** True for factory kit presets (shown coral, like crossfades). */
  preset?: boolean;
}

/** One recorded note inside a phrase blob (pure JSON, no audio). */
export interface PhraseEvent {
  midi: number;
  /** Seconds from phrase start. */
  time: number;
  /** Note length in seconds. */
  dur: number;
}

// A "blob": a short recorded sequence of notes (pitch, timing, duration,
// overlaps) played through one sound. Stays entirely on the JSON level.
export interface PhraseSlot {
  id: string;
  kind: "phrase";
  name: string;
  color: string;
  patch: Patch;
  events: PhraseEvent[];
  /** Total phrase length in seconds. */
  length: number;
}

export type PaletteSlot = PatchSlot | PhraseSlot;
export type Palette = (PaletteSlot | null)[];

const STORAGE_KEY = "wap.palette";

export function genId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

// Stays inside the brand cyan/purple family — the old amber/green/pink hues
// predate the "one accent, position differentiates" decision.
export function colorFor(patch: Patch): string {
  switch (patch.oscillator.type) {
    case "square": return "#b388ff";
    case "sawtooth": return "#3ad6ff";
    case "pulse": return "#8a6fd0";
    default: return "#00bfff";
  }
}

export function emptyPalette(): Palette {
  return new Array<PaletteSlot | null>(PALETTE_SIZE).fill(null);
}

function coerceSlot(raw: unknown): PaletteSlot | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Record<string, unknown>;
  if (!s.patch || typeof s.patch !== "object") return null; // drops old audio samples
  const patch = s.patch as Patch;
  const id = typeof s.id === "string" ? s.id : genId();
  const name = typeof s.name === "string" ? s.name : patch.name ?? "snd";
  const color = typeof s.color === "string" ? s.color : colorFor(patch);
  if (s.kind === "phrase" && Array.isArray(s.events)) {
    const events = (s.events as unknown[]).filter(
      (e): e is PhraseEvent =>
        !!e &&
        typeof (e as PhraseEvent).midi === "number" &&
        typeof (e as PhraseEvent).time === "number" &&
        typeof (e as PhraseEvent).dur === "number",
    );
    return { id, kind: "phrase", name, color, patch, events, length: typeof s.length === "number" ? s.length : 0 };
  }
  // Patch slot (also the legacy shape, which had no `kind`).
  return {
    id, kind: "patch", name, color, patch,
    lastMidi: typeof s.lastMidi === "number" ? s.lastMidi : 60,
    ...(s.preset === true ? { preset: true } : {}),
  };
}

export function loadPalette(): Palette {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        const palette = emptyPalette();
        for (let i = 0; i < Math.min(arr.length, PALETTE_SIZE); i++) {
          palette[i] = coerceSlot(arr[i]);
        }
        return palette;
      }
    }
  } catch {
    // ignore corrupt/unavailable storage
  }
  return emptyPalette();
}

export function savePalette(palette: Palette): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(palette));
  } catch {
    // ignore (e.g. quota exceeded when samples are large)
  }
}
